<template>
    <div class="page-update-notification">
        <!--<div class="page-update-notification__recipients">
            <strong>Recipients:</strong>
            {{ recipientNames }}
        </div>-->

        <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
        </div>

        <div v-if="errorMessage && !showModal" class="alert alert-danger">
            {{ errorMessage }}
        </div>

        <button type="button"
                class="btn btn-outline-primary"
                @click="openModal">
            <i class="fa-regular fa-envelope mr-1"></i>
            Send page update
        </button>

        <div v-if="showModal"
             class="page-update-notification__modal">
            <div class="page-update-notification__modal-backdrop"
                 @click="closeModal"></div>

            <div class="page-update-notification__modal-dialog" @click.stop>
                <div class="page-update-notification__modal-content">
                    <div class="page-update-notification__modal-header">
                        <h3>Send page update</h3>

                        <button type="button"
                                class="btn-close"
                                aria-label="Close"
                                @click="closeModal">
                        </button>
                    </div>

                    <div class="page-update-notification__modal-body">
                        <div class="mb-3">
                            <strong>Recipients:</strong>
                            {{ recipientNames }}
                        </div>

                        <!--<div class="form-group">
                            <label for="page-update-subject">
                                Subject
                            </label>

                            <input id="page-update-subject"
                                   v-model.trim="subjectDraft"
                                   type="text"
                                   class="form-control">
                        </div>-->

                        <div class="form-group mt-3">
                            <label for="page-update-message">
                                Message
                            </label>

                            <textarea id="page-update-message"
                                      v-model.trim="messageDraft"
                                      class="form-control"
                                      rows="8">
                            </textarea>
                        </div>

                        <div class="d-inline-flex form-check mt-3">
                            <input id="page-update-send-test"
                                   v-model="sendTestEmail"
                                   type="checkbox"
                                   class="form-check-input me-2">

                            <label class="form-check-label"
                                   for="page-update-send-test">
                                Send test email
                            </label>
                        </div>

                        <div v-if="sendTestEmail"
                             class="form-group mt-3">
                            <label for="page-update-test-recipients">
                                Test recipients
                            </label>

                            <input id="page-update-test-recipients"
                                   v-model.trim="testRecipients"
                                   type="text"
                                   class="form-control"
                                   placeholder="name@example.com, another@example.com">

                            <small class="form-text text-muted">
                                Separate multiple email addresses with commas.
                            </small>
                        </div>

                        <div v-if="successMessage"
                             class="alert alert-success mt-3">
                            {{ successMessage }}
                        </div>

                        <div v-if="errorMessage"
                             class="alert alert-danger mt-3">
                            {{ errorMessage }}
                        </div>
                    </div>

                    <div class="page-update-notification__modal-footer">
                        <button type="button"
                                class="btn btn-link"
                                :disabled="sending"
                                @click="closeModal">
                            Cancel
                        </button>

                        <button type="button"
                                class="btn btn-primary"
                                :disabled="sending || !canSend"
                                @click="send">
                            {{ sendButtonText }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onBeforeUnmount, computed, ref, watch } from 'vue';

    const props = defineProps({
        pageId: {
            type: String,
            required: true
        },

        pageTitle: {
            type: String,
            default: ''
        },

        currentUrl: {
            type: String,
            required: true
        },

        userName: {
            type: String,
            default: ''
        },

        subject: {
            type: String,
            default: ''
        },

        message: {
            type: String,
            default: ''
        },

        groups: {
            type: Array,
            required: true
        },

        sendUrl: {
            type: String,
            required: true
        }
    });

    const showModal = ref(false);
    const sending = ref(false);
    const subjectDraft = ref(props.subject ?? '');
    const messageDraft = ref(props.message ?? '');
    const testRecipients = ref('');
    const sendTestEmail = ref(false);
    const errorMessage = ref('');
    const successMessage = ref('');

    const recipientNames = computed(() => {
        return props.groups.map(x => x.Name).join(', ');
    });

    const canSend = computed(() => {
        if (!subjectDraft.value || !messageDraft.value) {
            return false;
        }

        if (sendTestEmail.value && !testRecipients.value) {
            return false;
        }

        return true;
    });

    const sendButtonText = computed(() => {
        if (sending.value) {
            return sendTestEmail.value
                ? 'Sending test…'
                : 'Sending…';
        }

        return sendTestEmail.value
            ? 'Send test email'
            : 'Send page update';
    });

    watch(showModal, isOpen => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
    });

    onBeforeUnmount(() => {
        document.body.style.overflow = '';
    });

    function openModal() {
        subjectDraft.value = props.subject ?? '';
        messageDraft.value = props.message ?? '';
        sendTestEmail.value = false;
        testRecipients.value = '';
        showModal.value = true;
        errorMessage.value = '';
        successMessage.value = '';
    }

    function closeModal() {
        if (sending.value) {
            return;
        }

        showModal.value = false;
        errorMessage.value = '';
    }

    async function send() {
        if (!canSend.value) {
            return;
        }

        errorMessage.value = '';
        sending.value = true;

        let payload = {
            PageId: props.pageId,
            PageTitle: props.pageTitle,
            CurrentUrl: props.currentUrl,
            Subject: subjectDraft.value,
            Message: messageDraft.value,
            TestRecipients: sendTestEmail.value
                ? testRecipients.value
                : '',
            GroupIds: props.groups.map(x => x.Id)
        };

        try {
            const response = await fetch(props.sendUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'RequestVerificationToken': getAntiForgeryToken()
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Unable to send page update (${response.status}).`);
            }

            // showModal.value = false;
            // successMessage.value = 'Page update notification sent.';
            if (sendTestEmail.value) {
                successMessage.value = 'Test email sent.';
            } else {
                showModal.value = false;
                successMessage.value = 'Page update notification sent.';
            }

        } catch (error) {
            errorMessage.value = error?.message || 'Unable to send the page update notification.';
        } finally {
            sending.value = false;
        }
    }

    function getAntiForgeryToken() {
        return document.querySelector('input[name="__RequestVerificationToken"]')?.value ?? '';
    }

</script>